import {
  CalendarEditActions,
  CalendarEditActionTypes,
  FetchCalendarSuccess,
  InitCalendarEditTemplate,
  SelectCalendarRange,
  SelectDay,
  SelectMonth,
  SelectWeekDays,
  SelectYear,
  UpdateValidityPeriodSuccess
} from '@app/pcs/actions/calendar-edit';
import {
  ApplyTrafficPeriodSuccess,
  TrafficPeriodSelectionActionTypes
} from '@app/pcs/actions/traffic-period-selection';
import { Calendar, CalendarDay, ValidityPeriod } from '@app/pcs/models';
import {
  findMonth,
  getDayAt,
  getDaysOfMonth,
  getDayValue,
  getMonthAt,
  getMonths,
  getNextSelection,
  isBetweenDays,
  isSelectable,
  setSelection,
  toggleDaySelection,
  updateWeekDaysSelection
} from '@app/pcs/reducer/calendar/base-calendar-edit-util';
import { createHandler, createReducer, mapIndexed } from '@util';
import * as R from 'ramda';

export interface Day {
  dayIndex: number;
  monthIndex: number;
}

export interface State {
  calendar: Calendar;
  initialCalendar: Calendar;
}

export const initialState: State = {
  calendar: null,
  initialCalendar: null
};

// Selectors
type GetCalendar = (s: State) => Calendar;
export const getCalendar: GetCalendar = R.prop('calendar');

// Handlers
const fetchCalendarSuccess = (state: State, {payload: calendar}: FetchCalendarSuccess): State => {
  const updatedCalendar = updateWeekDaysSelection(calendar);
  return R.mergeRight(state, {
    calendar: updatedCalendar,
    initialCalendar: updatedCalendar
  });
};

const initCalendarTemplate = (state: State, {payload: calendar}: InitCalendarEditTemplate): State => {
  const updatedCalendar = calendar ? updateWeekDaysSelection(calendar) : calendar;
  return R.mergeRight(state, {
    calendar: updatedCalendar,
    initialCalendar: updatedCalendar
  });
};

const updateCalendar = (state: State, {payload: calendar}: UpdateValidityPeriodSuccess) =>
  R.mergeRight(state, {
    calendar: updateWeekDaysSelection(calendar)
  });

const selectCalendarDay = (state: State, {payload: {dayIndex, monthIndex}}: SelectDay) => {
  const {calendar} = state;
  const months = getMonths(calendar);
  const month = getMonthAt(monthIndex, months);
  const days = getDaysOfMonth(month);
  const day = getDayAt(dayIndex, days);

  const updatedCalendar = R.mergeRight(calendar, {
    months: R.update(
      monthIndex,
      R.mergeRight(month, {
        days: R.update<any, any>(dayIndex, toggleDaySelection(day), days)
      }),
      months
    )
  });

  return R.mergeRight(state, {
    calendar: updateWeekDaysSelection(updatedCalendar)
  });
};

const selectCalendarMonth = (state: State, {payload: monthIndex}: SelectMonth) => {
  const {calendar} = state;
  const months = getMonths(calendar);
  const month = getMonthAt(monthIndex, months);
  const days = getDaysOfMonth(month);
  const nextSelection = getNextSelection([month]);

  const updatedCalendar = R.mergeRight(calendar, {
    months: R.update(
      monthIndex,
      R.mergeRight(month, {
        days: R.map<any, any>(setSelection(nextSelection), days)
      }),
      months
    )
  });

  return R.mergeRight(state, {
    calendar: updateWeekDaysSelection(updatedCalendar)
  });
};

const selectYear = (state: State, {payload: year}: SelectYear) => {
  const {calendar} = state;
  const months = getMonths(calendar);
  const monthsOfYear = R.filter(R.compose(R.equals(year), R.prop('year')), months);
  const nextSelection = getNextSelection(monthsOfYear);

  const updateSelection: any = (month) => {
    if (month.year === year) {
      const days = getDaysOfMonth(month);
      return R.mergeRight(month, {
        days: R.map(setSelection(nextSelection), days)
      });
    }
    return month;
  };

  const updatedCalendar = R.mergeRight(calendar, {
    months: R.chain(updateSelection, months)
  });

  return R.mergeRight(state, {
    calendar: updateWeekDaysSelection(updatedCalendar)
  });
};

const selectWeekDays = (state: State, {payload: selectedDay}: SelectWeekDays) => {
  const {calendar} = state;
  const months = getMonths(calendar);
  const weekDayValue = getDayValue(selectedDay);
  const isWeekDay: any = R.compose(R.equals(weekDayValue), R.prop('dayValue'));
  const nextSelection = getNextSelection(months, isWeekDay);

  const updateDaySelection: any = (day: CalendarDay) =>
    isWeekDay(day) ? setSelection(nextSelection, day) : day;

  const updateMonthSelection: any = month =>
    R.evolve({
      days: R.chain(updateDaySelection)
    }, month);

  const updatedCalendar = R.mergeRight(calendar, {
    months: R.chain(updateMonthSelection, months)
  });

  return R.mergeRight(state, {
    calendar: updateWeekDaysSelection(updatedCalendar)
  });
};

const resetSelection = (state: State): State =>
  R.mergeRight(state, {calendar: state.initialCalendar});


const updateValidityPeriod = (state: State, validityPeriod: ValidityPeriod) => {
  const {calendar} = state;
  const {periodStart, periodEnd} = validityPeriod;
  const {months} = calendar;
  const allDays = R.flatten(R.map(getDaysOfMonth, months));

  const firstMonth = findMonth(periodStart, months);
  const lastMonth = findMonth(periodEnd, months);

  const vpStartIndex = firstMonth.startIndex + periodStart[2] - 1;
  const vpEndIndex = lastMonth.startIndex + periodEnd[2];

  const updateDaySelection: any = ({index: dayIndex}: CalendarDay) =>
    dayIndex < vpStartIndex || dayIndex >= vpEndIndex
      ? R.mergeRight(allDays[dayIndex], {selectable: false, selected: false, editable: false})
      : R.mergeRight(allDays[dayIndex], {selectable: true});

  const updateMonthSelection: any = month =>
    R.evolve({
      days: R.chain(updateDaySelection)
    }, month);

  const updatedCalendar = R.mergeRight(calendar, {
    months: R.chain(updateMonthSelection, months),
    validityPeriod
  });

  return R.mergeRight(state, {
    calendar: updateWeekDaysSelection(updatedCalendar)
  });
};

const applyTrafficPeriod = (state: State, {payload}: ApplyTrafficPeriodSuccess): State => {
  const {months, calendarWatermark, weekdays} = payload;
  const updatedCalendar = R.mergeRight(state.calendar, {
    months,
    calendarWatermark,
    weekdays
  });

  return updateValidityPeriod(
    R.mergeRight(state, {calendar: updatedCalendar}),
    updatedCalendar.validityPeriod
  );
};

const selectDaysRange = (state: State, {payload: {start, end}}: SelectCalendarRange): State => {
  const {calendar} = state;
  const {months} = calendar;
  const allDays = R.flatten(R.map(getDaysOfMonth, months));
  const selected = allDays[start].selected;
  const updateMonthSelection: any = month =>
    R.mergeRight(month, {
      days: mapIndexed((day) => isBetweenDays(day, start, end)
      && isSelectable(day)
        ? R.mergeRight(day, {selected})
        : day, month.days)
    });

  const updatedCalendar = R.mergeRight(calendar, {
    months: R.chain(updateMonthSelection, months)
  });

  return R.mergeRight(state, {
    calendar: updateWeekDaysSelection(updatedCalendar)
  });
};

const fetchCalendarSuccessHandler = createHandler(CalendarEditActionTypes.FetchCalendarSuccess, fetchCalendarSuccess);
const initCalendarTemplateHandler = createHandler(CalendarEditActionTypes.InitCalendarEditTemplate, initCalendarTemplate);
const selectCalendarDayHandler = createHandler(CalendarEditActionTypes.SelectDay, selectCalendarDay);
const selectCalendarMonthHandler = createHandler(CalendarEditActionTypes.SelectMonth, selectCalendarMonth);
const selectYearHandler = createHandler(CalendarEditActionTypes.SelectYear, selectYear);
const selectWeekDaysHandler = createHandler(CalendarEditActionTypes.SelectWeekDays, selectWeekDays);
const updateValidityPeriodHandler = createHandler(CalendarEditActionTypes.UpdateValidityPeriodSuccess, updateCalendar);
const applyTrafficPeriodHandler = createHandler(TrafficPeriodSelectionActionTypes.ApplyTrafficPeriodSuccess, applyTrafficPeriod);
const resetSelectionHandler = createHandler(CalendarEditActionTypes.ResetSelection, resetSelection);
const selectDaysRangeHandler = createHandler(CalendarEditActionTypes.SelectCalendarRange, selectDaysRange);

export const reducer = createReducer<State, CalendarEditActions>([
  fetchCalendarSuccessHandler,
  initCalendarTemplateHandler,
  selectCalendarDayHandler,
  selectCalendarMonthHandler,
  selectYearHandler,
  selectWeekDaysHandler,
  updateValidityPeriodHandler,
  applyTrafficPeriodHandler,
  resetSelectionHandler,
  selectDaysRangeHandler
], initialState);
